import { useUser } from '@faro/auth';
import { FaroChip } from '@faro/design-system';
import { BookIcon, Import2Icon, OptimizationIcon, StudyDefinitionIcon, UsersIcon } from '@faro/icons';
import { TenantConfiguration } from '@faro/study-designer-model';
import { useTenantConfigurationQuery } from '@faro/study-designer-services';
import { ComponentProps, ReactNode } from 'react';
import { useAppPermissions } from '../../package/auth';
import SideNav from '../SideNav/SideNav';
import { LinkData } from '../SideNav/SideNavHeader/SideNavHeader';
import styles from './NavLayout.module.scss';

export type NavLayoutProps = ComponentProps<'div'>;

/**
 * This layout renders the Nav.
 *
 * If it is the top nav, it is across the top of the page and renders
 * children within the remaining body space.
 *
 * For studyspaces, it renders a side nav bar instead.
 *
 * @param props TopNavBar properties
 * @constructor
 */
export function NavLayout(props: NavLayoutProps): ReactNode {
    const { className = '', children, ...rest } = props;

    const user = useUser();
    const permissions = useAppPermissions();
    const tenantConfig = useTenantConfigurationQuery();
    const isStudyDefinitionAdmin = user.roles?.includes('STUDY-DEFINITION-ADMIN');
    const appLinks = getGrantedAppLinks(permissions.grantedApps, isStudyDefinitionAdmin, tenantConfig.data);

    return (
        <div {...rest} className={`${styles.layoutContainer} ${className} ${styles.spaces}`}>
            <div className={styles.bodyContainer}>
                <div className={styles.body}>{children}</div>
            </div>
            <SideNav user={user} headerLinks={appLinks} />
        </div>
    );
}

function getGrantedAppLinks(
    grantedApps: Set<string>,
    isStudyDefinitionAdmin: boolean,
    tenantConfig?: TenantConfiguration
): LinkData[] {
    const links: LinkData[] = [];
    if (grantedApps.has('study-spaces')) {
        links.push({
            id: 'studySpaces',
            label: 'Study Spaces',
            icon: <StudyDefinitionIcon />,
            url: '/spaces',
        });
    }
    if (grantedApps.has('content-library')) {
        links.push({
            id: 'contentLibrary',
            label: 'Content Library',
            icon: <BookIcon />,
            url: '/libraries',
        });
    }
    if (grantedApps.has('user-management')) {
        links.push({
            id: 'userManagement',
            label: 'User Management',
            icon: <UsersIcon />,
            url: '/user-management',
        });
    }
    if (grantedApps.has('protocol-import') && isStudyDefinitionAdmin) {
        const versions = tenantConfig?.protocolImportVersions ?? [];
        if (versions.includes('soa')) {
            links.push({
                id: 'protocolImport',
                intercomLabel: 'SoA Import',
                label: (
                    <>
                        <span className={styles.link}>SoA Import</span>
                        <FaroChip color="info">Lab</FaroChip>
                    </>
                ),
                icon: <Import2Icon />,
                url: '/protocol-import',
            });
        }
        if (versions.includes('protocol')) {
            links.push({
                id: 'ProtocolImport_Lab',
                intercomLabel: 'Protocol Import',
                label: (
                    <>
                        <span className={styles.link}>Protocol Import</span>
                        <FaroChip color="info">Lab</FaroChip>
                    </>
                ),
                icon: <Import2Icon />,
                url: '/full-protocol-import',
            });
        }
    }
    if (
        grantedApps.has('protocol-optimization') &&
        tenantConfig?.protocolOptimizationEnabled === true &&
        isStudyDefinitionAdmin
    ) {
        links.push({
            id: 'protocolOptimization',
            intercomLabel: 'Protocol Optimization',
            label: (
                <>
                    <span className={styles.link}>Protocol Optimization</span>
                    <FaroChip color="info">Lab</FaroChip>
                </>
            ),
            icon: <OptimizationIcon />,
            url: '/protocol-optimization',
        });
    }
    return links;
}
